import { useEffect, useMemo, type ReactNode } from "react";
import type { OcPortalConfig } from "../lib/oc-api";

interface OcPortalLayoutProps {
  config: OcPortalConfig;
  children: ReactNode;
}

// Parse a hex color like "#0ea5e9" or "0ea5e9" or "#fff" into "R G B".
// Returns null when the input is not a valid hex color.
function ocHexToRgbTriplet(hex: string | undefined): string | null {
  if (!hex) return null;
  let value = hex.trim().replace(/^#/, "");
  if (value.length === 3) {
    value = value
      .split("")
      .map((c) => c + c)
      .join("");
  }
  if (!/^[0-9a-fA-F]{6}$/.test(value)) return null;
  const r = parseInt(value.slice(0, 2), 16);
  const g = parseInt(value.slice(2, 4), 16);
  const b = parseInt(value.slice(4, 6), 16);
  return `${r} ${g} ${b}`;
}

export function OcPortalLayout({ config, children }: OcPortalLayoutProps) {
  const ocPrimaryRgb = useMemo(
    () => ocHexToRgbTriplet(config.branding.primaryColor) ?? "14 165 233",
    [config.branding.primaryColor],
  );

  useEffect(() => {
    document.title = `${config.tenantName} — Password Reset`;
  }, [config.tenantName]);

  const ocRootStyle = { ["--oc-primary-rgb" as any]: ocPrimaryRgb } as React.CSSProperties;

  return (
    <div
      className="min-h-full w-full bg-slate-50 text-slate-900 flex flex-col"
      style={ocRootStyle}
    >
      <main className="flex-1 flex items-start sm:items-center justify-center px-4 py-10">
        <div className="w-full max-w-md">
          <header className="flex flex-col items-center mb-6">
            {config.branding.logoUrl ? (
              <img
                src={config.branding.logoUrl}
                alt={`${config.tenantName} logo`}
                className="h-14 w-auto mb-3 object-contain"
              />
            ) : (
              <div className="h-14 w-14 mb-3 rounded-xl bg-brand flex items-center justify-center text-white text-xl font-semibold shadow-sm">
                {config.tenantName.charAt(0).toUpperCase()}
              </div>
            )}
            <h1 className="text-lg font-semibold text-slate-900 text-center">
              {config.tenantName}
            </h1>
            <p className="text-sm text-slate-500 mt-1">Password Reset</p>
          </header>

          <section className="bg-white rounded-2xl shadow-sm ring-1 ring-slate-200 p-6 sm:p-8">
            {children}
          </section>

          {config.branding.supportContactText ? (
            <footer
              className="oc-support-text mt-6 text-center text-xs text-slate-500 [&_a]:underline"
              dangerouslySetInnerHTML={{ __html: config.branding.supportContactText }}
            />
          ) : null}
        </div>
      </main>
    </div>
  );
}

export default OcPortalLayout;
